import {
  List,
  Datagrid,
  TextField,
  DateField,
  NumberField,
  EditButton,
  ShowButton,
  Filter,
  SelectInput,
  TextInput,
  SearchInput,
  useListContext,
  ChipField,
  FunctionField,
  Show,
  SimpleShowLayout,
  TabbedShowLayout,
  Tab,
  useShowController,
  Button,
  useNotify,
  useRefresh,
  CreateButton,
  DeleteButton,
  useDataProvider,downloadCSV,
} from "react-admin";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Stack,
  Divider,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress
} from "@mui/material";
import {
  Call,
  CallMade,
  CallReceived,
  PhoneCallback,
  AccessTime,
  Person,
  Notes,
  Add,
} from "@mui/icons-material";
import { useState } from 'react';
import { CallNotes } from "./CallNotes";
import jsonExport from 'jsonexport/dist';


const ExportProgressDialog = ({ open, page, totalPages }) => {
  const progress = (page / totalPages) * 100;

  return (
    <Dialog open={open}>
      <DialogTitle>Export in Progress</DialogTitle>
      <DialogContent>
        <Typography>{`Exporting page ${page} / ${totalPages}`}</Typography>
        <LinearProgress variant="determinate" value={progress} />
      </DialogContent>
    </Dialog>
  );
};
const convertToCSV = (rows) => {
  if (!rows.length) return "";
  const header = Object.keys(rows[0]).join(",");
  const data = rows.map(r =>
    Object.values(r).map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")
  );
  return [header, ...data].join("\n");
};

export const usePaginatedExporter = () => {
  const dataProvider = useDataProvider();
  const [progress, setProgress] = useState({ open: false, page: 0, totalPages: 0 });

  const exporter = async () => {
    const perPage = 10;

    // first call to get total
    const first = await dataProvider.getList("call-records", {
      pagination: { page: 1, perPage },
      sort: { field: "id", order: "ASC" },
      filter: {},
    });

    let allRecords = [...first.data];
    const totalPages = Math.ceil(first.total / perPage);

    setProgress({ open: true, page: 1, totalPages });

    for (let page = 2; page <= totalPages; page++) {
          const { data } = await dataProvider.getList("call-records", {
        pagination: { page, perPage },
        sort: { field: "id", order: "ASC" },
        filter: {},
      });
      allRecords = [...allRecords, ...data];
      setProgress({ open: true, page, totalPages }); // ✅ update instead of new snackbar
    }

    // build CSV
    const csvData = allRecords.map(record => ({
      ID: record.id,
      Time: record.callDate,
      Caller: record.src,
      Destination: record.dst,
      Type: record.callType,
      Status: record.disposition,
      Duration: record.duration,
      Agent: record.agentId,
      Quality: record.qualityScore,
      Notes: record.notes?.length > 0 ? record.notes.length : 0,
    }));

    const csv = convertToCSV(csvData);
      jsonExport(csvData, { rowDelimiter: ',' }, (err, csv) => {
        if (err) {
            console.error(err);
            return;
        }
        // Add UTF-8 BOM to fix Arabic characters in Excel
        const bom = '\uFEFF';
        downloadCSV(bom + csv, "call_records.csv");
    });

    setProgress({ open: false, page: 0, totalPages: 0 }); // close dialog
  };

  // return exporter + UI
  return {
    exporter,
    progressUI: (
      <ExportProgressDialog
        open={progress.open}
        page={progress.page}
        totalPages={progress.totalPages}
      />
    ),
  };
};

// Utility functions
const formatDuration = (seconds) => {
  if (!seconds) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

// Custom Call Type Chip
const CallTypeChip = ({ record }) => {
  const getCallTypeProps = (type) => {
    switch (type) {
      case 'incoming':
        return { icon: <CallReceived />, color: 'success', label: 'Incoming' };
      case 'outgoing':
        return { icon: <CallMade />, color: 'primary', label: 'Outgoing' };
      case 'internal':
        return { icon: <Call />, color: 'secondary', label: 'Internal' };
      default:
        return { icon: <PhoneCallback />, color: 'default', label: type };
    }
  };

  const props = getCallTypeProps(record?.callType);
  return (
    <Chip
      icon={props.icon}
      label={props.label}
      color={props.color}
      size="small"
      variant="outlined"
    />
  );
};

// Custom Status Chip
const StatusChip = ({ record }) => {
  const getStatusProps = (status) => {
    switch (status?.toLowerCase()) {
      case 'answered':
        return { color: 'success', label: 'Answered' };
      case 'no answer':
        return { color: 'warning', label: 'No Answer' };
      case 'busy':
        return { color: 'error', label: 'Busy' };
      case 'failed':
        return { color: 'error', label: 'Failed' };
      default:
        return { color: 'default', label: status };
    }
  };

  const props = getStatusProps(record?.disposition);
  return (
    <Chip
      label={props.label}
      color={props.color}
      size="small"
    />
  );
};

// CallRecords Filter Component
const CallRecordsFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn placeholder="Search calls..." />
    <SelectInput
      source="callType"
      choices={[
        { id: 'incoming', name: 'Incoming' },
        { id: 'outgoing', name: 'Outgoing' },
        { id: 'internal', name: 'Internal' },
      ]}
      placeholder="All call types"
    />
    <SelectInput
      source="disposition"
      choices={[
        { id: 'ANSWERED', name: 'Answered' },
        { id: 'NO ANSWER', name: 'No Answer' },
        { id: 'BUSY', name: 'Busy' },
        { id: 'FAILED', name: 'Failed' },
      ]}
      placeholder="All statuses"
    />
    <TextInput source="src" label="From" />
    <TextInput source="dst" label="To" />
    <TextInput source="agentId" label="Agent" />
  </Filter>
);

// Call Info Card Component
const CallInfoCard = ({ record }) => (
  <Card sx={{ mb: 2 }}>
    <CardContent>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Caller
              </Typography>
              <Typography variant="h6" fontWeight="medium">
                {record.src}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Destination
              </Typography>
              <Typography variant="h6" fontWeight="medium">
                {record.dst}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Agent
              </Typography>
              <Typography variant="body1">
                {record.agentId || '–'}
              </Typography>
            </Box>
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Duration
              </Typography>
              <Typography variant="h6" fontWeight="medium">
                {formatDuration(record.duration)}
              </Typography>
              {record.billableSeconds > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {formatDuration(record.billableSeconds)} billable
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Queue
              </Typography>
              <Typography variant="body1">
                {record.queueName || '–'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Quality Score
              </Typography>
              <Typography variant="body1">
                {record.qualityScore ? `${record.qualityScore}/10` : '–'}
              </Typography>
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

// Main List Component
export const CallRecordsList = (props) => {
  const { exporter, progressUI } = usePaginatedExporter();
  return (
    <List
      {...props}
      filters={<CallRecordsFilter />}
      sort={{ field: 'callDate', order: 'DESC' }}
      perPage={25}
      exporter={exporter}

      sx={{
        '& .RaList-content': {
          boxShadow: 'none',
          backgroundColor: 'transparent',
        }
      }}
    >
      {progressUI}
      <Datagrid
        rowClick="show"
        sx={{
          '& .RaDatagrid-row': {
            borderLeft: '3px solid transparent',
            '&:hover': {
              borderLeftColor: 'primary.main',
              backgroundColor: 'action.hover',
            },
          },
        }}
      >
        <DateField
          source="callDate"
          label="Time"
          showTime
          sx={{ fontWeight: 'medium', minWidth: 140 }}
        />

        {/* <FunctionField
          label="Call"
          render={(record) => (
            <Box sx={{ minWidth: 180 }}>
              <Typography variant="body2" fontWeight="medium">
                {record.src}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                to {record.dst}
              </Typography>
            </Box>
          )}
        /> */}
        <TextField
          source="src"
          label="src"
          sx={{ minWidth: 100 }}
          emptyText="–"
        />
        <TextField
          source="dst"
          label="dst"
          sx={{ minWidth: 100 }}
          emptyText="–"
        />

        {/* <FunctionField
          label="Type & Status"
          render={(record) => (
            <Stack spacing={0.5} sx={{ minWidth: 120 }}>
              <CallTypeChip record={record} />
              <StatusChip record={record} />
            </Stack>
          )}
        /> */}

        <FunctionField
          label="Type"
          render={(record) => (
            <CallTypeChip record={record} />
          )}
        />
        <FunctionField
          label="Status"
          render={(record) => (
            <StatusChip record={record} />
          )}
        />

        <FunctionField
          label="Duration"
          render={(record) => (
            <Box sx={{ textAlign: 'center', minWidth: 80 }}>
              <Typography variant="body2" fontWeight="medium">
                {formatDuration(record.duration)}
              </Typography>
              {record.billableSeconds > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {formatDuration(record.billableSeconds)} billable
                </Typography>
              )}
            </Box>
          )}
        />

        <TextField
          source="agentId"
          label="Agent"
          sx={{ minWidth: 100 }}
          emptyText="–"
        />

        <FunctionField
          label="Notes"
          render={(record) => (
            <Box sx={{ minWidth: 60, textAlign: 'center' }}>
              {record.notes?.length > 0 ? (
                <Chip
                  label={record.notes.length}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  –
                </Typography>
              )}
            </Box>
          )}
        />

        {/* <ShowButton label="" size="small" />
        <EditButton label="" size="small" /> */}
      </Datagrid>
    </List>
  );
};

// Main Show Component
export const CallRecordShow = (props) => {
  const { record } = useShowController(props);

  if (!record) return null;

  return (
    <Show {...props} sx={{ maxWidth: 1200, margin: '0 auto' }}>
      <TabbedShowLayout>
        <Tab label="Overview" icon={<Call />}>
          {/* Call Summary */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <CallTypeChip record={record} />
                <StatusChip record={record} />
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  ID: {record.uniqueId}
                </Typography>
              </Stack>

              <CallInfoCard record={record} />

              {/* Additional Details */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Call Details
                    </Typography>
                    <Stack spacing={1}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Reason
                        </Typography>
                        <Typography variant="body2">
                          {record.callReason || 'Not specified'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Additional Info
                        </Typography>
                        <Typography variant="body2">
                          {record.callDetails || 'No additional details'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Technical Info
                    </Typography>
                    <Stack spacing={1}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Channel
                        </Typography>
                        <Typography variant="body2" fontFamily="monospace" fontSize="12px">
                          {record.channel || '–'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Destination Channel
                        </Typography>
                        <Typography variant="body2" fontFamily="monospace" fontSize="12px">
                          {record.dstChannel || '–'}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Tab>

        <Tab label="Notes" icon={<Notes />} path="notes">
          <CallNotes record={record} />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};

// Export for use in React Admin
export default {
  list: CallRecordsList,
  show: CallRecordShow,
};